<template>
  <div class="d-flex flex-column flex-fill">
    <b-card class="mb-4" v-for="(score, i) in scores" :key="`${i}-${score.id}`">
      <b-form-group label-cols="12" label-cols-md="3" label="Label (Required)" description="This name appears on the Leaderboard column header">
        <b-form-input v-model="score.label"/>
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Value (Required)"
        description="These are the number of points awarded">
        <b-form-input required type="number" @mousewheel="e => e.preventDefault()" v-model="score.value"/>
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Description" description="This appears in the explainations of the score system">
        <b-form-input v-model="score.description"/>
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Daily Cap" description="The maximum amount of points a user can collect a day.">
        <b-form-input type="number" v-model="score.dailyMaximum" />
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Weekly Cap" description="The maximum amount of points a user can collect a week.">
        <b-form-input type="number" v-model="score.weeklyMaximum" />
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Icon" description="This appears in the explainations of the score system">
        <b-form-radio v-for="icon in scoreIcons" :key="`${i}-${icon}`" :value="icon" v-model="score.icon" :name="`${score.id}-icon`">
          <font-awesome-icon :icon="icon" />
        </b-form-radio>
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Event Names">
        <checkbox-group :name="`${score.id}-scores`" :options="scoreEventNames" v-model="score.eventNames"/>
      </b-form-group>
      <b-form-group label-cols="12" label-cols-md="3" label="Disabled"
        description="Mark this as disabled to hide from the leaderboard and not collect any points for the events">
          <b-form-checkbox v-model="score.disabled"/>
      </b-form-group>
      <div class="d-flex flex-row justify-content-end">
        <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(true, i)">Up</b-btn>
        <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(false, i)">Down</b-btn>
        <b-btn variant="outline-danger" @click="() => scores.splice(i, 1)">Remove</b-btn>
      </div>
    </b-card>
    <div class="d-flex flex-row justify-content-end">
      <b-btn variant="outline-primary" @click="() => scores.push({ id: Date.now(), eventNames: [], value: 1 })">Add New</b-btn>
    </div>
    <div class="mt-3 pt-3 d-flex flex-row justify-content-end border-top">
      <b-btn variant="outline-primary" @click="input()">Update Scores</b-btn>
    </div>
  </div>
</template>

<script>
import CheckboxGroup from '@/components/settings/CheckboxGroup';
import { mapPreferences } from '@/mixins/root';

export default {
  components: {
    CheckboxGroup,
  },
  props: {
    value: {
      type: Array,
    },
  },
  data: () => ({
    scores: [],
  }),
  computed: {
    ...mapPreferences([ 'canEmployeeApply' ]),
    scoreIcons: () => ([
      'plug',
      'link',
      'file-alt',
      'users',
      'handshake',
    ]),
    scoreEventNames() {
      const eventNames = [
        {
          text: 'Application Created',
          value: 'APPLICATION_CREATED',
          tooltip: 'Awarded when a user\'s candidate applies for a role',
        },
        {
          text: 'Application Confirmed',
          value: 'APPLICATION_CONFIRMED',
          tooltip: 'Awarded when a user\'s candidate application is confirmed (this is only used when the application is submitted in your ATS after a redirect from Real Links.)',
        },
        {
          text: 'Application Interviewed',
          value: 'APPLICATION_INTERVIEW',
          tooltip: 'Awarded when a user\'s candidate is moved to Interview',
        },
        {
          text: 'Application Hired',
          value: 'APPLICATION_HIRED',
          tooltip: 'Awarded when a user\'s candidate is moved to Hired',
        },
        {
          text: 'Content Shared',
          value: 'CONTENT_SHARED',
          tooltip: 'Awarded when a user shares a content link (each time)',
        },
        {
          text: 'Content Scheduled',
          value: 'CONTENT_SCHEDULE_SHARE_FIRST',
          tooltip: 'Awarded when a user schedules a piece of content for sharing',
        },
        {
          text: 'Content Viewed',
          value: 'CONTENT_SHARE_VISITED',
          tooltip: 'Awarded when a visitor views content (each time)',
        },
        {
          text: 'Content Viewed (Unique)',
          value: 'CONTENT_SHARE_UNIQUE_VISITED',
          tooltip: 'Awarded every time when a new visitor views content',
        },
        {
          text: 'Content Viewed for the First Time',
          value: 'CONTENT_SHARE_FIRST_VISITED',
          tooltip: 'Awarded when the piece of content is visited for the first time',
        },
        {
          text: 'Job Shared',
          value: 'JOB_SHARED',
          tooltip: 'Awarded when a user shares a job (each time)',
        },
        {
          text: 'Job Scheduled',
          value: 'JOB_SCHEDULE_SHARE_FIRST',
          tooltip: 'Awarded when a user schedules a jobs for sharing',
        },
        {
          text: 'Job Viewed',
          value: 'SHARE_VISITED',
          tooltip: 'Awarded when a visitor views a share link (each time)',
        },
        {
          text: 'Job Viewed (Unique)',
          value: 'SHARE_UNIQUE_VISITED',
          tooltip: 'Awarded every time when a new visitor views a share link',
        },
        {
          text: 'Job Viewed for the First Time',
          value: 'SHARE_FIRST_VISITED',
          tooltip: 'Awarded when first visitor views a share link',
        },
        {
          text: 'Referral Suggested',
          value: 'REFERRAL_SUGGESTED',
          tooltip: 'Awarded when a user chooses to refer a contact',
        },
        {
          text: 'Jam Referral Suggested',
          value: 'EVENT_REFERRAL_CREATED',
          tooltip: 'Awarded when a user chooses to refer a contact during a referral jam',
        },
        {
          text: 'Referral Viewed',
          value: 'REFERRAL_VISITED',
          tooltip: 'Awarded when a visitor views a share link (each time)',
        },
        {
          text: 'Referral Viewed (Unique)',
          value: 'REFERRAL_UNIQUE_VISITED',
          tooltip: 'Awarded every time a new visitor views a share link',
        },
        {
          text: 'Referral Viewed for the First Time',
          value: 'REFERRAL_FIRST_VISITED',
          tooltip: 'Awarded when first visitor views a share link',
        },
        {
          text: 'LinkedIn Connections Browsed',
          value: 'JOB_BROWSE_LINKEDIN',
          tooltip: 'Awarded when a user searches for connections on LinkedIn (each time)',
        },
      ];
      return [
        ...eventNames,
        ...(this.canEmployeeApply ? [
          {
            text: 'Internal Application Created',
            value: 'INTERNALAPPLICATION_CREATED',
            tooltip: 'Awarded when a user applies for an internal role',
          },
          {
            text: 'Internal Application Confirmed',
            value: 'INTERNALAPPLICATION_CONFIRMED',
            tooltip: 'Awarded when a user\'s application is confirmed for an internal role (this is only used when the application is submitted in your ATS after a redirect from Real Links.)',
          },
          {
            text: 'Internal Application Interviewed',
            value: 'INTERNALAPPLICATION_INTERVIEW',
            tooltip: 'Awarded when a user\'s internal application is moved to Interview',
          },
          {
            text: 'Internal Application Hired',
            value: 'INTERNALAPPLICATION_HIRED',
            tooltip: 'Awarded when a user\'s internal application is moved to Hired',
          }
        ] : []),
      ];
    },
  },
  created() {
    this.scores = JSON.parse(JSON.stringify([ ...(Array.isArray(this.value) ? this.value : []) ])).map(el => {
      delete el.__typename;
      return el;
    });
  },
  methods: {
    input() {
      const scores = [ ...(Array.isArray(this.scores) ? this.scores : []) ].map(el => {
        el.id = `${el.label}-${(Math.round(Math.random() * 10000))}`;
        el.value = parseInt(el.value);
        el.dailyMaximum = parseInt(el.dailyMaximum);
        el.weeklyMaximum = parseInt(el.weeklyMaximum);
        return el;
      });
      this.$emit('input', scores);
    },
    scoreMoveItem(up, i) {
      if (i === 0 && up) {
        return false;
      }
      if (i === this.scores.length - 1 && !up) {
        return false;
      }

      const j = up ? i - 1 : i + 1;
      const [ value ] = this.scores.splice(i, 1);
      this.scores.splice(j, 0, value);
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-score-system.vue